import {
  type IDelivery,
  type IUpdateRecipientAndDeliveryResponse
} from '@/types/IFetchInfoOrderRecipient';
import { type IVendorItem } from '../../types/IFetchOrderVendorParams';
import { api } from '../../utils/api';
import {
  CustomError,
  getErrorData,
  type IErrorData,
  type IGenericResponseRoot
} from '../../utils/getErrorData';
import {
  type UpdatePaymentStatusResponse,
  type IFetchUpdatePaymentStatus
} from '@/types/IFetchUpdatePaymentStatus';
import {
  type PaymentCreationResponse,
  type IOrderPaymentCreationResponse
} from '@/types/IOrderPaymentCreation';
import { type IFetchOrderInfoOnChangeVendor } from '@/types/IFetchOrderInfoOnChangeVendor';
import { type ICreatePaymentData } from '@/types/ICreatePaymentData';
import { type PaymentProvidersEnum } from '@/types/PaymentProvidersEnum';
import { cleanObject } from '../../utils/cleanObject';
import { type IOrderRecipient } from '@/types/IOrder';
import { type IVendorNamesResponse } from '@/types/IVendorNamesResponse';
import {
  IUpdateOrderDeliveryPayload,
  IUpdateOrderDeliveryResponse
} from '@/types/IUpdateOrderDelivery';
import {
  IOffersByStatusResponse,
  OfferStatus
} from '@/types/IVendorWithCountOffers';

// Получаем имена поставщиков
export const getVendorNames = async (): Promise<IVendorItem[]> => {
  const response = await api.get<IGenericResponseRoot<IVendorNamesResponse>>(
    '/admin/offer-vendors'
  );
  const items: IVendorItem[] = response.data.response?.items ?? [];
  return items;
};

// обновляем наименование поставщика в заказе
export const updateOrderOfferVendor = async (
  orderUuid: string,
  orderOffer: number,
  vendorId: number
): Promise<IFetchOrderInfoOnChangeVendor> => {
  const url = `/admin/order/${orderUuid}/update/offer/${orderOffer}`;
  const response = await api.post<{ response: IFetchOrderInfoOnChangeVendor }>(
    url,
    { vendor: vendorId }
  );
  return response.data.response;
};

export const updateRecipientAndDelivery = async (
  // orderUuid: string,
  orderId: number,
  recipient: Partial<IOrderRecipient>,
  delivery: Partial<IDelivery>
): Promise<IUpdateRecipientAndDeliveryResponse | IErrorData> => {
  const url = `/admin/order/${orderId}/update/recipient-and-delivery`;

  const cleanedRecipient = cleanObject(recipient, ['email']);
  const cleanedDelivery = cleanObject(delivery);

  try {
    const response = await api.post<
      IGenericResponseRoot<IUpdateRecipientAndDeliveryResponse>
    >(url, {
      recipient: cleanedRecipient,
      delivery: cleanedDelivery
    });

    if (response.data.response === null) {
      throw new CustomError(`Данные для заказа ${orderId} не были найдены`);
    }

    return response.data.response;
  } catch (error) {
    const errorData = getErrorData(error);
    return errorData;
  }
};

// обновляем информацию о статусе платежа
export const updatePaymentStatus = async (
  paymentID: number,
  statusName: string
): Promise<UpdatePaymentStatusResponse> => {
  const url = `/admin/payment/${paymentID}/status/${statusName}`;
  const data: IFetchUpdatePaymentStatus = await api.post(url);
  return data.response;
};

// создание платежа по айди клиента
export const createPaymentForOrder = async (
  orderUuid: string,
  providerName: PaymentProvidersEnum,
  clientID: number | null | undefined,
  amount: number | null,
  comment: string
): Promise<PaymentCreationResponse> => {
  const url = '/admin/payment';
  const paymentData: ICreatePaymentData = {
    order: orderUuid,
    provider: providerName,
    forAccount: clientID,
    amount,
    comment
  };
  const response = await api.post<IOrderPaymentCreationResponse>(
    url,
    paymentData
  );

  if (
    response?.data?.response === undefined ||
    response?.data?.response === null
  ) {
    throw new Error('Не удалось создать платёж');
  }
  return response.data.response;
};

export const updateOrderDelivery = async (
  orderUuid: string,
  payload: IUpdateOrderDeliveryPayload
): Promise<IUpdateOrderDeliveryResponse | IErrorData> => {
  const url = `/admin/order/${orderUuid}/update/delivery`;

  const cleanedPayload = cleanObject(payload);

  try {
    const response = await api.post<
      IGenericResponseRoot<IUpdateOrderDeliveryResponse>
    >(url, cleanedPayload);

    if (response.data.response === null) {
      throw new CustomError(`Данные для заказа ${orderUuid} не были обновлены`);
    }

    return response.data.response;
  } catch (error) {
    const errorData = getErrorData(error);
    return errorData;
  }
};

// Получаем офферы по статусу
export const getOffersCountByStatusAndVendor = async (
  offerStatus: OfferStatus
): Promise<IOffersByStatusResponse | IErrorData> => {
  const url = `/admin/vendor-order/vendors/offers/${offerStatus}`;

  try {
    const response =
      await api.get<IGenericResponseRoot<IOffersByStatusResponse>>(url);

    if (response.data.response === null) {
      throw new CustomError(
        `Данные для статуса ${offerStatus} не были найдены`
      );
    }

    return response.data.response;
  } catch (error) {
    const errorData = getErrorData(error);
    return errorData;
  }
};
